// Chip primitive — ported from the TB-EMS SPA.
//
// Used for: severity badges, granularity badges, "live" status pills,
// segmented toggle groups, dismissible filters. One visual register so the
// chrome reads coherent across surfaces.
//
// Differences from TB-EMS's version:
//   - no i18n dependency — the dismiss control's aria-label comes from the
//     optional `dismissLabel` prop (default "Dismiss").
//   - tinted tones derive from a single `--ems-*` var via color-mix, so
//     every chip stays theme-aware under [data-theme="dark"].
//   - a non-interactive chip renders a <span>, not a disabled <button> —
//     keeps the HTML valid when a chip sits inside another control.

import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

export type ChipTone =
  | "neutral"
  | "brand"
  | "import"
  | "export"
  | "critical"
  | "major"
  | "minor"
  | "warning"
  | "stale"
  | "live";

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> & {
  tone?: ChipTone;
  size?: "xs" | "sm";
  leading?: ReactNode;
  trailing?: ReactNode;
  /** When true, renders a trailing × that fires `onDismiss`. */
  dismissible?: boolean;
  onDismiss?: () => void;
  onClick?: () => void;
  /** aria-label for the dismiss control. */
  dismissLabel?: string;
  children: ReactNode;
};

const SIZE_CLASS: Record<NonNullable<Props["size"]>, string> = {
  xs: "text-[10px] px-1.5 py-0.5 gap-1",
  sm: "text-xs px-2 py-0.5 gap-1.5",
};

// Each tinted tone is driven by one --ems-* var; color-mix keeps the soft
// fill + border tracking the var so dark mode flips for free.
const TONE_VAR: Record<Exclude<ChipTone, "neutral" | "brand">, string> = {
  // Grid-direction tones — the canonical SCADA pair (DSGN-05): direction
  // words always wear direction colours, never ok/warn status colours.
  import: "--ems-grid-import",
  export: "--ems-grid-export",
  critical: "--ems-alarm-critical",
  major: "--ems-alarm-major",
  minor: "--ems-alarm-minor",
  warning: "--ems-alarm-warning",
  // Stale/frozen DATA — grey-amber, never an alarm-severity colour: a
  // frozen value is a data-quality state, not a warning condition.
  stale: "--ems-stale",
  live: "--ems-ok",
};

function toneStyle(tone: ChipTone): CSSProperties | undefined {
  if (tone === "neutral") return undefined;
  if (tone === "brand") return { background: "var(--ems-brand)" };
  const v = TONE_VAR[tone];
  return {
    color: `var(${v})`,
    background: `color-mix(in srgb, var(${v}) 12%, transparent)`,
    borderColor: `color-mix(in srgb, var(${v}) 35%, transparent)`,
  };
}

export function Chip({
  tone = "neutral",
  size = "sm",
  leading,
  trailing,
  dismissible,
  onDismiss,
  onClick,
  dismissLabel = "Dismiss",
  className,
  children,
  ...rest
}: Props) {
  const interactive = Boolean(onClick) || dismissible;
  const classes = cn(
    "inline-flex items-center font-medium rounded-full border whitespace-nowrap leading-none",
    tone === "neutral" && "bg-surface-2 text-text-muted border-edge",
    tone === "brand" && "text-white border-transparent",
    interactive && "cursor-pointer transition-colors",
    SIZE_CLASS[size],
    className,
  );

  const inner = (
    <>
      {tone === "live" && (
        <span
          aria-hidden
          className="ems-pulse-ring inline-block h-1.5 w-1.5 rounded-full"
          style={{ background: "var(--ems-ok)" }}
        />
      )}
      {leading}
      <span>{children}</span>
      {trailing}
      {dismissible && (
        <span
          role="button"
          aria-label={dismissLabel}
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss?.();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onDismiss?.();
            }
          }}
          className="-mr-0.5 ml-1 inline-flex items-center justify-center rounded-full transition-opacity hover:opacity-70"
        >
          <X className="h-3 w-3" strokeWidth={2.25} />
        </span>
      )}
    </>
  );

  if (interactive) {
    return (
      <button type="button" onClick={onClick} style={toneStyle(tone)} className={classes} {...rest}>
        {inner}
      </button>
    );
  }
  return (
    <span style={toneStyle(tone)} className={classes}>
      {inner}
    </span>
  );
}
